import type { Course, PricingPlanFeature } from "../api";

/**
 * `GR-TRU01`, `docs/11` §7.5: a course page promises only what the course's
 * own record backs.
 *
 * Some claims have a field behind them, and the page used to print them from
 * copy whatever the field said: the CP course promised a certificate in six
 * places while `certificateEnabled` was false. Each claim here names the field
 * that decides it; while the field does not back it, every line that makes the
 * claim is left out, typed items by their type and free lines (a chip, an
 * include, a highlight, an FAQ entry, a plan feature) by the words they use.
 * The copy itself is untouched in the database, so switching the field on
 * brings every line back with no edit.
 *
 * Cohort dates, prices and the refund line are not here: they are never copy,
 * they render from their fields and an empty field leaves the line out
 * (`CourseCohortLine`, `refundPolicyText_*`). Long prose (the description) is
 * not filtered by line; `course-content/tools/claims-audit.mjs` lists it for
 * an editor instead.
 */
export type CourseClaim = "certificate" | "liveClasses" | "lifetimeAccess";

/** The field behind each claim. */
export function courseBacksClaim(course: Pick<Course, "certificateEnabled" | "delivery_type" | "accessDurationDays">, claim: CourseClaim): boolean {
  switch (claim) {
    case "certificate":
      return course.certificateEnabled === true;
    case "liveClasses":
      return course.delivery_type !== "recorded";
    case "lifetimeAccess":
      return course.accessDurationDays == null;
  }
}

/**
 * The words that make each claim, in English and in Bangla. Kept to what can
 * only mean the claim: "live class", not "live", which a recorded course may
 * honestly say of a support session.
 */
const CLAIM_WORDS: Record<CourseClaim, RegExp> = {
  certificate: /certificat|সার্টিফিকেট|সনদ/i,
  liveClasses: /\blive[\s-]*class|লাইভ\s*ক্লাস|live\s*ক্লাস/i,
  lifetimeAccess: /\blifetime\b|\bfor life\b|লাইফটাইম|সারাজীবন|সারা\s*জীবন|আজীবন/i,
};

const CLAIMS: CourseClaim[] = ["certificate", "liveClasses", "lifetimeAccess"];

/** The claims this course's record does not back. */
export function unbackedClaims(course: Pick<Course, "certificateEnabled" | "delivery_type" | "accessDurationDays">): Set<CourseClaim> {
  return new Set(CLAIMS.filter((claim) => !courseBacksClaim(course, claim)));
}

/** Whether any of these texts makes a claim in `unbacked`. */
export function makesUnbackedClaim(unbacked: ReadonlySet<CourseClaim>, ...texts: Array<string | null | undefined>): boolean {
  if (unbacked.size === 0) return false;
  return texts.some((text) => {
    if (!text) return false;
    for (const claim of unbacked) if (CLAIM_WORDS[claim].test(text)) return true;
    return false;
  });
}

/** What's Included items carry a type that is itself a claim. */
const BENEFIT_TYPE_CLAIM: Partial<Record<string, CourseClaim>> = {
  certificate: "certificate",
  live_classes: "liveClasses",
  lifetime_access: "lifetimeAccess",
};

/** Chips and the sidebar list carry an icon that is itself a claim. */
const ICON_KEY_CLAIM: Partial<Record<string, CourseClaim>> = {
  certificate: "certificate",
};

function typedClaimUnbacked(
  unbacked: ReadonlySet<CourseClaim>,
  table: Partial<Record<string, CourseClaim>>,
  key: string | undefined,
): boolean {
  const claim = key ? table[key] : undefined;
  return claim !== undefined && unbacked.has(claim);
}

type LabelledItem = { label_en: string; label_bn?: string; description_en?: string; description_bn?: string };

function keepLabelled<T extends LabelledItem>(
  items: T[] | undefined,
  unbacked: ReadonlySet<CourseClaim>,
  typedUnbacked: (item: T) => boolean,
): T[] | undefined {
  if (!items) return items;
  return items.filter(
    (item) =>
      !typedUnbacked(item) &&
      !makesUnbackedClaim(unbacked, item.label_en, item.label_bn, item.description_en, item.description_bn),
  );
}

function keepLines(lines: string[] | undefined, unbacked: ReadonlySet<CourseClaim>): string[] | undefined {
  return lines?.filter((line) => !makesUnbackedClaim(unbacked, line));
}

/**
 * The course with every line its record does not back taken out. The course
 * page renders this, never the raw payload, so the sections, their visibility
 * checks, the section menu and the FAQ's structured data all agree.
 */
export function withBackedClaims(course: Course): Course {
  const unbacked = unbackedClaims(course);
  if (unbacked.size === 0) return course;

  const presentation = course.coursePresentation;
  const certificate = presentation?.certificateAndCareer?.certificate;

  return {
    ...course,
    faq: course.faq?.filter(
      (item) => !makesUnbackedClaim(unbacked, item.question_en, item.question_bn, item.answer_en, item.answer_bn),
    ),
    highlights_en: keepLines(course.highlights_en, unbacked),
    highlights_bn: keepLines(course.highlights_bn, unbacked),
    whatYouWillLearn_en: keepLines(course.whatYouWillLearn_en, unbacked),
    whatYouWillLearn_bn: keepLines(course.whatYouWillLearn_bn, unbacked),
    ...(presentation
      ? {
          coursePresentation: {
            ...presentation,
            ...(presentation.quickValueStrip
              ? {
                  quickValueStrip: {
                    ...presentation.quickValueStrip,
                    items: keepLabelled(presentation.quickValueStrip.items, unbacked, (item) =>
                      typedClaimUnbacked(unbacked, ICON_KEY_CLAIM, item.iconKey),
                    ),
                  },
                }
              : {}),
            ...(presentation.sidebarIncludes
              ? {
                  sidebarIncludes: {
                    ...presentation.sidebarIncludes,
                    items: keepLabelled(presentation.sidebarIncludes.items, unbacked, (item) =>
                      typedClaimUnbacked(unbacked, ICON_KEY_CLAIM, item.iconKey),
                    ),
                  },
                }
              : {}),
            ...(presentation.includedBenefits
              ? {
                  includedBenefits: {
                    ...presentation.includedBenefits,
                    items: keepLabelled(presentation.includedBenefits.items, unbacked, (item) =>
                      typedClaimUnbacked(unbacked, BENEFIT_TYPE_CLAIM, item.type),
                    ),
                  },
                }
              : {}),
            ...(presentation.learningSupport
              ? {
                  learningSupport: {
                    ...presentation.learningSupport,
                    items: keepLabelled(presentation.learningSupport.items, unbacked, () => false),
                  },
                }
              : {}),
            ...(presentation.certificateAndCareer && certificate && unbacked.has("certificate")
              ? {
                  certificateAndCareer: {
                    ...presentation.certificateAndCareer,
                    certificate: { ...certificate, enabled: false },
                  },
                }
              : {}),
          },
        }
      : {}),
  };
}

/** A plan's features without the lines its course does not back. */
export function backedPlanFeatures(
  features: PricingPlanFeature[],
  unbacked: ReadonlySet<CourseClaim>,
): PricingPlanFeature[] {
  return features.filter((feature) => !makesUnbackedClaim(unbacked, feature.text_en, feature.text_bn));
}
